import {_} from 'lodash';
import * as ko from "knockout";
import {
    ICompanySyncstep,
    ICompanySyncstepFilter,
    IFormTemplate,
    IMessageData,
    IRuleset,
    ISyncCycleStep,
    ISyncstep,
    ISystem
} from "../../../../index";
import {MutableCompany} from "../MutableCompany";
import {MutableSyncCycle} from "./MutableSyncCycle";
import {TCrudMode, TTabValue} from "./index";
import {global, lm} from "../../../../appContext";
import {IComboboxV2Options} from "../../../combobox/v2";
import {createServiceListPromise} from "../../../combobox/v2-sources";
import {MAX_RESULTS_PER_PAGE, Permission} from "../../../../constants";
import {getSearchParamsFn, getSearchSystemParams} from "../../../../util/request";
import {ErrorManager} from "../../../../error/ErrorManager";
import {Config} from "./parameters/Config";
import {SyncStepValidator} from "../../../syncsteps/SyncStepValidator";
import {MutableSyncstepFilters} from "./export-filter/MutableSyncstepFilters";
import {ErrorObject} from "../../../../error/ErrorObject";
import {createErrorMessage, decodeAndStripHtml} from "../../../../util/misc";
import {StatusManager} from "../StatusManager";
import {TBtnHeader, TSyncStatus} from "..";
import {nsUtils} from "../nsUtils";
import {IDropdownButton} from "../../../button";
import {UPDATING_MESSAGE} from "../../../../constants/Language";
import {getHash, isRoutePermissionOkay} from "../../../../constants/RouteDefinitions";
import {convertFromServerString} from "../../../../util/datetime";
import {nsFile} from "../nsFile";
import {nsSync} from "../../../../util/nsSync";
import {getTabHash} from "../../../../util/pageShellRoute";
import {withHashQuery} from "../../../../util/hashQuery";
import getAnyValue = nsUtils.getAnyValue;

interface IMutableCompanySyncstep {
    readonly active: KnockoutObservable<boolean | null>
    readonly customerkey: KnockoutObservable<string | null>
    readonly description: KnockoutObservable<string | null>
    readonly domain: KnockoutObservable<string | null>
    readonly eHubForm: KnockoutObservableArray<KnockoutObservable<IFormTemplate | null>>
    readonly filters: KnockoutObservableArray<KnockoutObservable<ICompanySyncstepFilter>>
    readonly id: KnockoutObservable<number | null>
    readonly importanchorsbeforesync: KnockoutObservable<boolean | null>
    readonly order: KnockoutObservable<number | null>
    readonly passwordexport: KnockoutObservable<boolean | null>
    readonly passwordexportdomain: KnockoutObservable<string | null>
    readonly prescriptblock: KnockoutObservable<string | null>
    readonly ruleset: KnockoutObservableArray<KnockoutObservable<IRuleset | null>>
    readonly scriptblock: KnockoutObservable<string | null>
    readonly syncstep: KnockoutObservable<ISyncstep | null>
    readonly system: KnockoutObservable<ISystem | null>
}

export class MutableCompanySyncStep {
    private readonly messageData: KnockoutObservable<IMessageData | null> = ko.observable(null)

    public started!: string | null;
    public ended!: string | null;
    public readonly isExpanded: KnockoutObservable<boolean> = ko.observable(false);
    private isDraggable: KnockoutObservable<boolean> = ko.observable(false);
    private isDragging: KnockoutObservable<boolean> = ko.observable(false);
    private readonly _crudMode: KnockoutObservable<TCrudMode | null> = ko.observable(null);
    public readonly tabValue: KnockoutObservable<TTabValue | null> = ko.observable(null);
    public readonly componentId: string
    private readonly em_tab_basics: ErrorManager = new ErrorManager()
    private readonly em_tab_configuration: ErrorManager = new ErrorManager()
    private readonly em_tab_script: ErrorManager = new ErrorManager()

    private readonly mutableSyncstepConfigs: KnockoutObservableArray<Config> = ko.observableArray()
    public readonly mutableSyncstepFilters: MutableSyncstepFilters

    public readonly index: KnockoutObservable<number | null> = ko.observable(null)
    public readonly prop: IMutableCompanySyncstep = {
        active: ko.observable(null),
        customerkey: ko.observable(null),
        description: ko.observable(null),
        domain: ko.observable(null),
        eHubForm: ko.observableArray<KnockoutObservable<IFormTemplate | null>>([]),
        filters: ko.observableArray<KnockoutObservable<ICompanySyncstepFilter>>([]),
        id: ko.observable(null),
        importanchorsbeforesync: ko.observable(null),
        order: ko.observable(null),
        passwordexport: ko.observable(null),
        passwordexportdomain: ko.observable(null),
        prescriptblock: ko.observable(null),
        ruleset: ko.observableArray<KnockoutObservable<IRuleset | null>>([]),
        scriptblock: ko.observable(null),
        syncstep: ko.observable(null),
        system: ko.observable(null),
    }
    private buttons: IDropdownButton[]
    private inlineButtons: IDropdownButton[]

    private readonly validator: SyncStepValidator

    private readonly errorObjects: Partial<Record<keyof IMutableCompanySyncstep, ErrorObject>> = {}
    private readonly mutableCompany: MutableCompany

    public readonly customScriptsDisabled = ko.pureComputed(() => !!this.statusManager.company?.hostedbyidentum)

    constructor(public companySyncstep: ICompanySyncstep, private importfilesyncstep: ISyncCycleStep | null, private mutableSyncCycle: MutableSyncCycle, private statusManager: StatusManager, private _index: number) {
        this.index(this._index)
        this.mutableCompany = statusManager.mutableCompany
        this.mutableSyncCycle = statusManager.mutableSyncCycle
        this.mutableSyncstepFilters = new MutableSyncstepFilters(companySyncstep, this.em_tab_script, statusManager)
        this.validator = new SyncStepValidator(companySyncstep, this.statusManager.company.eformforeadm)
        this.tabValue('basics')
        this.componentId = 'mutable-company-syncstep-' + companySyncstep.syncstep.id

        this.buttons = [
            this.btnEdit,
            this.btnSubmit,
            this.btnUndo,
            this.btnCancel,
            this.btnDelete,
            this.btnExportCompanySyncstep,
            this.btnChangelogDetails_menu,
            this.btnObjectsWithInvalidAnchor,
        ]
        this.inlineButtons = [
            this.btnChangelogDetails_inline,
            this.btnDelete,
            this.btnCancel,
            this.btnUndo,
            this.btnSubmit,
            this.btnEdit,
        ]

        if (this.importfilesyncstep?.logfilepaths) {
            const btnDownloadFiles = (() => {
                const fileNames = this.importfilesyncstep.logfilepaths.split('|');
                const numFiles = fileNames?.length ?? 0;
                const params = {
                    numFiles: numFiles,
                    fileNames: fileNames,
                    date: convertFromServerString(this.importfilesyncstep.ended).format('YYYY-MM-DD'),
                    companySyncstep: this.companySyncstep,
                    importfilesyncstep: this.importfilesyncstep,
                    syncstepname: nsSync.getSyncstepName(this.companySyncstep.syncstep),
                    importfile: this.statusManager.importfile,
                    companyid: this.statusManager.company.id,
                };
                const btn = {
                    cls: 'btn-separated btn-action',
                    icon: 'fa-download',
                    label: {
                        k: 'synccycle.step.btn.downloadfiles',
                        obj: params,
                    },
                    onClick: () => {
                        nsFile
                            .downloadFile({
                                filename: lm.t({
                                    k: `synccycle.step.btn.downloadfiles.filename`,
                                    obj: params,
                                }) ?? '',
                                params: {
                                    id: this.importfilesyncstep!.id,
                                    orgnr: this.statusManager.company.id,
                                    filepath: this.importfilesyncstep!.logfilepaths,
                                },
                                servicename: 'getfiles',
                            })
                            .catch((reason) => {
                                this.isExpanded(true)
                                this.messageData(createErrorMessage(String(reason)))
                            })
                    },
                    validator: () => {
                        const a = !this.isCrudMode('either', 'update', 'delete')
                        const b = this.mutableSyncCycle.crudMode() == 'read'
                        const c = !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate()
                        const d = !!this.importfilesyncstep?.logfilepaths
                        return a && b && c && d
                    },
                }
                return btn
            })()
            this.buttons.push(btnDownloadFiles)
        }

        this.init()
        this.crudMode(companySyncstep.id ? 'read' : 'create')

        this.errorObjects.domain = new ErrorObject()
        this.errorObjects.domain.addValidator({
            fn: () => !!this.prop.domain(),
            message: 'Invalid value',
            prerequisite: () => this.isCrudMode('either', 'update')
        })
        this.em_tab_basics.addErrorObject(this.errorObjects.domain)

        this.errorObjects.eHubForm = new ErrorObject()
        this.errorObjects.eHubForm.addValidator({
            fn: () => this.prop.eHubForm().reduce((ret: boolean, ft: KnockoutObservable<IFormTemplate | null>) => !!ft()?.id && ret, true),
            message: 'Invalid form template',
            prerequisite: () => !!this.prop.eHubForm()?.length && this.isCrudMode('either', 'update'),
        })
        this.em_tab_script.addErrorObject(this.errorObjects.eHubForm)

        this.errorObjects.ruleset = new ErrorObject()
        this.errorObjects.ruleset.addValidator({
            fn: () => this.prop.ruleset().reduce((ret: boolean, rs: KnockoutObservable<IRuleset | null>) => !!rs()?.id && ret, true),
            message: 'Invalid ruleset',
            prerequisite: () => !!this.prop.ruleset().length && this.isCrudMode('either', 'update'),
        })
        this.em_tab_script.addErrorObject(this.errorObjects.ruleset)
    }

    public init() {
        this.messageData(null)

        let key: keyof IMutableCompanySyncstep
        for (key in this.prop) {
            const nullValue: any = ko.isObservableArray(this.prop[key])
                ? []
                : null
            this.prop[key](nullValue)
        }

        this.started = this.importfilesyncstep?.started ?? null
        this.ended = this.importfilesyncstep?.ended ?? null

        const keys = Object.keys(this.prop) as (keyof ICompanySyncstep)[];
        keys.forEach((k: keyof ICompanySyncstep) => {
            if (ko.isObservableArray(this.prop[k])) {
                const values = this.companySyncstep[k] as any[] ?? [];
                const observableArray = this.prop[k] as KnockoutObservableArray<any>;
                observableArray(values.map(v => ko.observable(v)))
            } else {
                const value = this.companySyncstep[k] as any;
                this.prop[k](value)
            }
        })

        this.mutableSyncstepConfigs([])
        this.mutableSyncstepFilters.init()
    }

    private getNameClass(): string {
        if (this.isCrudMode('either', 'update') && !this.isCrudMode('local', 'delete')) {
            const a = this.em_tab_basics.validate();
            const b = this.em_tab_configuration.validate();
            const c = this.em_tab_script.validate();
            return a && b && c
                ? ''
                : 'error'
        }
        return ''
    }

    /**
     * Single source for Status-column badge text + shared .ds-status-pill classes.
     * Alert panels still key off getStatus() directly (same status ids).
     */
    private getStatusBadge(): {
        pillCls: string,
        text: string,
    } {
        switch (this.getStatus()) {
            case 'executed':
                return {
                    pillCls: 'ds-status-pill--success ds-status-pill--with-dot',
                    text: 'synccycle.step.status.executed',
                }
            case 'waiting':
                return {
                    pillCls: 'ds-status-pill--neutral ds-status-pill--with-dot',
                    text: 'synccycle.step.status.waiting',
                }
            case 'executing':
                return {
                    pillCls: 'ds-status-pill--info ds-status-pill--with-dot ds-status-pill--pulse',
                    text: 'synccycle.step.status.executing',
                }
            case 'halted':
                return {
                    pillCls: 'ds-status-pill--danger ds-status-pill--with-dot',
                    text: 'synccycle.step.status.error',
                }
            case 'executedwitherrors':
                // Warning (amber), not Failed — matches expanded .alert.executedwitherrors
                return {
                    pillCls: 'ds-status-pill--warning ds-status-pill--with-dot',
                    text: 'synccycle.step.status.executedwitherrors',
                }
            case 'unknown':
                return {
                    pillCls: 'ds-status-pill--neutral ds-status-pill--with-dot',
                    text: 'synccycle.step.status.waiting',
                }
        }
    }

    public getStatus(): TSyncStatus {
        if (this.importfilesyncstep?.halted) {
            return 'halted'
        }
        if (!this.importfilesyncstep?.started) {
            return 'waiting'
        }
        if (this.importfilesyncstep?.ended) {
            if (this.getTranslationObj().numErrors) {
                return 'executedwitherrors'
            } else {
                return 'executed'
            }
        }
        return 'executing'
    }

    public getTranslationObj() {
        const historiesWithErrors = this.statusManager.importfile.objecthistories
            .filter(o => o.syncstepid == this.companySyncstep.syncstep.id)
            .filter(o => !!o.lasterror)
        const numErrors = historiesWithErrors.length
        const numObjects = Object.keys(_.groupBy(historiesWithErrors, (o: typeof historiesWithErrors[number]) => o.userid)).length
        const syncstepname = nsSync.getSyncstepName(this.companySyncstep.syncstep)
        const error: string | null = this.companySyncstep.syncstep.name == this.statusManager.company.lastsyncstep?.syncstepname
            ? decodeAndStripHtml(this.statusManager.company.lastsyncstep.result)
            : null
        return {error, numErrors, numObjects, syncstepname}
    }

    private getRowBindings() {
        return {
            attr: {
                draggable: this.isDraggable,
                'aria-expanded': this.isExpanded,
            },
            css: {
                'is-dragging': this.isDragging,
                'is-draggable': this.isDraggable,
                'is-active': this.prop.active,
                'is-editing-locally': this.isEditingLocally,
            },
            cls: ko.pureComputed(() => 'crud-' + this.crudMode()),
            event: {
                dragstart: (draggingSource: MutableCompanySyncStep, e: JQuery.DragEvent) => {
                    this.isDragging(true)
                    this.mutableSyncCycle.draggingSource(draggingSource)
                    const dataTransfer = e.originalEvent?.dataTransfer
                    if (dataTransfer) {
                        dataTransfer.effectAllowed = 'move';

                        // avoid ghost image of draggable row
                        const canvas = document.createElement("canvas");
                        dataTransfer.setDragImage(canvas, 25, 25);
                    }

                    return true
                },
                dragenter: (mutableCompanySyncStep: MutableCompanySyncStep, e: JQuery.DragEvent) => {
                    e.preventDefault()
                    const element = e.delegateTarget;

                    const draggingSource = this.mutableSyncCycle.draggingSource()
                    const draggingTarget = ko.contextFor(element).$data as MutableCompanySyncStep;

                    if (!draggingTarget || draggingSource == draggingTarget) {
                        this.mutableSyncCycle.draggingTarget(null)
                        this.mutableSyncCycle.draggingTargetElement(null)
                        return true
                    }

                    this.mutableSyncCycle.draggingTarget(draggingTarget)
                    this.mutableSyncCycle.draggingTargetElement(element)

                    return false
                },
                dragover: (mutableCompanySyncStep: MutableCompanySyncStep, e: JQuery.DragEvent) => {
                    e.preventDefault()
                    const src = this.mutableSyncCycle.draggingSource()
                    const trg = this.mutableSyncCycle.draggingTarget()
                    const el = this.mutableSyncCycle.draggingTargetElement()
                    if (src && trg && el) {
                        const isDraggingDownwards = src.index()! < trg.index()!
                        const rect = el.getBoundingClientRect();
                        const clientY = e.clientY ?? 0;
                        const offsetY = clientY - rect.top;
                        const halfHeight = el.offsetHeight / 2;
                        const isPassedHalfway = isDraggingDownwards
                            ? offsetY > halfHeight
                            : offsetY < halfHeight
                        if (isPassedHalfway) {
                            this.mutableSyncCycle.swap()
                        } else {
                            // Not passed halfway - nothing to do (yet)
                        }
                    }
                },
                dragend: (mutableCompanySyncStep: MutableCompanySyncStep, e: JQuery.DragEvent) => {
                    this.mutableSyncCycle.draggingSource(null)
                    this.mutableSyncCycle.draggingTarget(null)
                    this.mutableSyncCycle.draggingTargetElement(null)
                    this.isDragging(false)
                    return true
                },
            }
        }
    }

    private getGripBindings() {
        return {
            disable: this.statusManager.isSubmitting,
            event: {
                mouseenter: (mutableCompanySyncStep: MutableCompanySyncStep, e: JQuery.MouseOverEvent) => {
                    if (!this.statusManager.isSubmitting()) {
                        this.isDraggable(true)
                    }
                },
                mouseout: (mutableCompanySyncStep: MutableCompanySyncStep, e: JQuery.MouseOutEvent) => {
                    this.isDraggable(false)
                },
            }
        }
    }

    private getTags = ko.pureComputed(() => {
        return [['synccycle.syncstep.syncstepid.label', this.companySyncstep.syncstep.id], ['synccycle.syncstep.companysyncstepid.label', this.companySyncstep.id], ['Order', this.prop.order()]]
    })

    private btnEdit: TBtnHeader = {
        cls: 'btn-action',
        onClick: () => {
            this.crudMode('update')
            this.isExpanded(true)
        },
        icon: 'fa-pencil',
        label: 'Edit',
        enabled: () => !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate(this),
        validator: () => {
            const a = this.isCrudMode('local', 'read');
            const b = !this.isCrudMode('global', 'update');
            const d = this.statusManager.permission() >= Permission.OrganizationAdmin
            return a && b && d
        },
    }

    private btnDelete: TBtnHeader = {
        cls: 'btn-destructive',
        enabled: ko.pureComputed(() => !this.isCrudMode('local', 'delete')),
        onClick: () => {
            if (this.isCrudMode('local', 'create')) {
                this.mutableSyncCycle.removeSyncstep(this)
            } else {
                this.crudMode('delete')
            }
        },
        icon: 'fa-trash-alt',
        label: 'Delete',
        validator: () => this.isCrudMode('global', 'update'),
    }

    private btnCancel: TBtnHeader = {
        cls: 'btn-action',
        label: 'Cancel',
        onClick: () => {
            this.init()
            this.crudMode('read')
        },
        icon: 'fa-ban',
        validator: () => {
            const a = this.isCrudMode('local', 'update');
            const b = !this.isCrudMode('global', 'update');
            return a && b
        },
    }

    private btnUndo: TBtnHeader = {
        cls: 'btn-action',
        label: 'Undo',
        onClick: () => {
            this.crudMode('read')
        },
        icon: 'fa-undo',
        validator: () => this.isCrudMode('local', 'delete'),
    }

    private btnSubmit: TBtnHeader = {
        cls: 'btn-success btn-action',
        label: 'Save',
        enabled: ko.pureComputed(() => {
            const a = this.isChanged()
            const b = this.isSyncstepValid()
            return a && b
        }),
        onClick: () => this.submit(),
        icon: 'fa-save',
        validator: () => {
            const a = this.isCrudMode('local', 'update');
            const b = !this.isCrudMode('global', 'update');
            return a && b
        },
    }

    private btnObjectsWithInvalidAnchor: TBtnHeader = {
        cls: 'btn-action',
        onClick: () => {
            global.dialogManager.addComponent({
                title: `synccycle.step.dialog.objectswithinvalidanchor`,
                isClosable: ko.observable(true),
                content: {
                    name: "component/object/dialog/invalid.anchor",
                    params: {syncstep: this.companySyncstep.syncstep},
                },
            });
        },
        icon: 'fa-anchor-invalid',
        label: 'synccycle.step.btn.objectswithinvalidanchor',
        validator: () => {
            const a = !this.isCrudMode('either', 'update', 'delete')
            const b = this.companySyncstep.syncstep.isexportable
            const c = !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate()
            const d = this.statusManager.permission() >= Permission.CompanyAdmin
            return a && b && c && d
        },
    }

    private btnExportCompanySyncstep: TBtnHeader = {
        cls: 'btn-separated btn-action',
        description: () => 'Only visible for super administrators',
        href: () => getHash('exportsyncstep', {id: this.companySyncstep.id}),
        icon: 'fa-file-export',
        label: 'synccycle.step.btn.exportcompanysyncstep',
        validator: () => {
            const a = !this.isCrudMode('either', 'update', 'delete')
            const b = this.mutableSyncCycle.crudMode() == 'read'
            const c = this.companySyncstep.syncstep.isexportable
            const d = this.statusManager.permission() >= Permission.SuperAdmin
            const e = !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate()
            return a && b && c && d && e
        },
    }

    private btnChangelogDetails_menu = this._getBtnChangelogDetails({
        label: () => {
            const items = this.statusManager.importfile.objecthistories.filter(o => o.syncstepid == this.companySyncstep.syncstep.id);
            return {
                k: 'synccycle.step.btn.changelog',
                obj: {
                    num: items.length,
                    syncstepname: nsSync.getSyncstepName(this.companySyncstep.syncstep),
                },
            }
        },
        validator: () => {
            const a = !this.isCrudMode('either', 'update', 'delete')
            const b = this.mutableSyncCycle.crudMode() == 'read'
            const c = isRoutePermissionOkay('importhistorydetails')
            const d = !!(this.statusManager.importfile.objecthistories.filter(o => o.syncstepid == this.companySyncstep.syncstep.id)?.length ?? 0)
            const e = !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate()
            return a && b && c && d && e
        },
    })

    private btnChangelogDetails_inline = this._getBtnChangelogDetails({
        label: 'See details',
        validator: () => {
            const a = !this.isCrudMode('either', 'update', 'delete')
            const b = this.mutableSyncCycle.crudMode() == 'read'
            const c = isRoutePermissionOkay('importhistorydetails')
            const d = !!(this.statusManager.importfile.objecthistories.filter(o => o.syncstepid == this.companySyncstep.syncstep.id)?.length ?? 0)
            const e = !this.mutableSyncCycle.isAnySyncstepCreateOrUpdate()
            const f = !!this.getTranslationObj().numObjects
            return a && b && c && d && e && f
        },
    })

    private _getBtnChangelogDetails(obj: Partial<TBtnHeader>): TBtnHeader {
        return _.defaultsDeep(obj, {
            cls: 'btn-action',
            href: () => {
                const hasErrors = [
                    'halted',
                    'executedwitherrors'
                ].includes(this.getStatus())
                return withHashQuery(
                    getTabHash('importhistoryhistory', {
                        importfileid: this.statusManager.importfile.id,
                    }),
                    {
                        ...(hasErrors ? {showOnlyErrors: true} : {}),
                        syncstepId: this.companySyncstep.syncstep.id,
                    },
                )
            },
            icon: 'fa-file-alt',
        })
    }

    public readonly isEditingLocally = ko.pureComputed(() => {
        return this._crudMode() === 'update' && this.mutableSyncCycle.crudMode() !== 'update'
    })

    public isCrudMode(context: 'local' | 'global' | 'both' | 'either', ...crudMode: TCrudMode[]): boolean {
        const local = this.isLocalCrudMode.apply(this, crudMode)
        const global = this.isGlobalCrudMode.apply(this, crudMode)
        switch (context) {
            case "local":
                return local
            case "global":
                return global
            case "both":
                return local && global
            case "either":
                return local || global
        }
    }

    private isLocalCrudMode(...crudMode: TCrudMode[]): boolean {
        const localCrudMode = this._crudMode()
        return localCrudMode !== null && crudMode.includes(localCrudMode)
    }

    private isGlobalCrudMode(...crudMode: TCrudMode[]): boolean {
        return crudMode.includes(this.mutableSyncCycle.crudMode())
    }

    public crudMode: KnockoutComputed<TCrudMode> = ko.computed({
        read: (): TCrudMode => {
            const globalCrudMode = this.mutableSyncCycle?.crudMode?.()
            const localCrudMode = this._crudMode()
            const priority: TCrudMode[] = ['delete', 'create', 'update', 'read']
            return priority.find(mode => {
                return localCrudMode == mode || globalCrudMode == mode
            }) ?? 'read'
        },
        write: (v: TCrudMode) => {
            this._crudMode(v)
        },
    })

    public isChanged() {
        const a = (Object.keys(this.prop) as (keyof IMutableCompanySyncstep)[]).reduce((ret: boolean, k: keyof IMutableCompanySyncstep): boolean => {
            let bool: boolean
            switch (k) {
                case "filters": {
                    bool = this.mutableSyncstepFilters.isChanged();
                    break
                }
                case 'eHubForm':
                case 'ruleset': {
                    const aValues = this.prop[k]() as any[] ?? []
                    const bValues = this.companySyncstep[k] as any[] ?? []
                    const a = aValues?.map(o => o()?.id)
                    const b = bValues?.map(o => o.id)
                    bool = a.toString() != b.toString();
                    break
                }
                case 'system': {
                    bool = this.prop[k]()?.id != this.companySyncstep[k]?.id;
                    break
                }
                case 'order': {
                    bool = this.prop.order() != (this.index() ?? 0) + 1
                    break
                }
                default: {
                    const a = this.prop[k]()?.toString?.() ?? this.prop[k]()
                    const b = this.companySyncstep[k]?.toString?.() ?? this.companySyncstep[k]
                    bool = a != b;
                    break
                }
            }
            return bool || ret
        }, false)
        const b = this.mutableSyncstepConfigs().reduce((ret: boolean, config: Config) => {
            const bool = config.isChanged()
            return bool || ret
        }, false)
        return a || b
    }

    private isSyncstepDeleted() {
        return this.crudMode() == 'delete'
    }

    public isSyncstepValid() {
        if (this.isCrudMode('local', 'delete')) {
            return true
        }
        const a = this.em_tab_basics.validate()
        const b = this.em_tab_configuration.validate()
        const c = this.em_tab_script.validate()
        return a && b && c
    }

    private onAddRulesetClick() {
        const rs: KnockoutObservable<IRuleset | null> = ko.observable(null);
        this.prop.ruleset.push(rs)
    }

    private onAddEHubFormClick() {
        const ft: KnockoutObservable<IFormTemplate | null> = ko.observable(null);
        this.prop.eHubForm.push(ft)
    }

    private getRulesetComboboxParams(ruleset: KnockoutObservable<IRuleset | null>): IComboboxV2Options<IRuleset> {
        const listSource = createServiceListPromise<IRuleset>({
            name: 'searchruleset',
            params: getSearchParamsFn('searchruleset')({
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }),
        })
        return {
            inputAttrs: {
                placeholder: lm.t('synccycle.syncstep.config.ruleset.hint'),
            },
            initialMatch: o => o.id == ruleset()?.id,
            option: {
                constrain: o => {
                    if (o.id == ruleset()?.id) {
                        return true
                    }
                    return !this.prop.ruleset().map(rs => rs()?.id).includes(o.id)
                },
                label: o => o.name,
            },
            promise: listSource.promise,
            onDispose: listSource.onDispose,
            selected: ruleset,
            inputStatus: (selected) => selected ? 'ok' : 'warning',
        }
    }

    private getEhubFormComboboxParams(formTemplate: KnockoutObservable<IFormTemplate | null>): IComboboxV2Options<IFormTemplate> {
        const listSource = createServiceListPromise<IFormTemplate>({
            name: 'searchformtemplate',
            params: getSearchParamsFn('searchformtemplate')({
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }),
        })
        return {
            inputAttrs: {
                placeholder: lm.t('synccycle.syncstep.config.ehubform.hint'),
            },
            initialMatch: o => o.id == formTemplate()?.id,
            option: {
                constrain: o => {
                    if (o.id == formTemplate()?.id) {
                        return true
                    }
                    return !this.prop.eHubForm().map(ft => ft()?.id).includes(o.id)
                },
                label: o => o.name,
            },
            promise: listSource.promise,
            onDispose: listSource.onDispose,
            selected: formTemplate,
            inputStatus: (selected) => selected ? 'ok' : 'warning',
        }
    }

    private getSystemCombboboxParams(): IComboboxV2Options<ISystem> {
        const listSource = createServiceListPromise<ISystem>({
            name: 'searchsystem',
            params: getSearchSystemParams({
                resultsprpage: MAX_RESULTS_PER_PAGE,
            }),
        })
        return {
            inputAttrs: {
                placeholder: lm.t('synccycle.syncstep.config.system.hint'),
            },
            initialMatch: o => o.id == this.companySyncstep.system?.id,
            option: {
                label: o => o.name,
            },
            promise: listSource.promise,
            onDispose: listSource.onDispose,
            selected: this.prop.system,
        }
    }

    private getTabClass(em: ErrorManager): string | undefined {
        if (this.isCrudMode('either', 'update') && !this.isCrudMode('local', 'delete')) {
            return em.validate() ? '' : 'error'
        }
        return undefined
    }

    private submitAllSyncstepParameters(companySyncstep: ICompanySyncstep = this.companySyncstep) {
        this.mutableSyncstepConfigs().forEach(config => config.companysyncstepid = companySyncstep.id)

        const deleted = this.mutableSyncstepConfigs().filter(o => o.isDeleted())
        const created = this.mutableSyncstepConfigs().filter(o => o.isCreated())
        const updated = this.mutableSyncstepConfigs().filter(o => o.isUpdated())

        const all: Config[] = [
            ...deleted,
            ...created,
            ...updated,
        ]

        const ret = !!all.length

        const doNext = (): Promise<any> => {
            const config = all.shift()
            if (!config) return Promise.resolve(null)
            return Promise.resolve()
                .then(() => {
                    if (config.isDeleted()) {
                        return this.statusManager.do_deletecompanysyncstepparameter(config.id)
                    }
                    if (config.isCreated()) {
                        return this.statusManager.do_createcompanysyncstepparameter(config.getSubmitData())
                    }
                    if (config.isChanged()) {
                        return this.statusManager.do_updatecompanysyncstepparameter(config.getSubmitData())
                    }
                })
                .then(() => doNext())
        }

        return Promise.resolve()
            .then(() => doNext())
            .then(() => ret)
            .catch((reason: string) => {
                this.isExpanded(true)
                this.statusManager.isSubmitting(false)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    public getSubmitData(): ICompanySyncstep {
        const ret = {} as ICompanySyncstep
        Object.keys(this.prop).forEach((key) => {
            const k = key as keyof ICompanySyncstep
            const val = getAnyValue<any>(this.prop[k as keyof IMutableCompanySyncstep])
            if (_.isArray(val)) {
                if (val.length) {
                    (ret[k] as any) = val.map((v: unknown) => getAnyValue(v))
                } else {
                    (ret[k] as any) = null
                }
            } else {
                switch (k) {
                    case 'order': {
                        (ret[k] as any) = (this.index() ?? 0) + 1
                        break
                    }
                    default: {
                        (ret[k] as any) = val
                        break
                    }
                }
            }
        })
        return ret
    }

    private submit() {
        return Promise.resolve()
            .then(() => {
                return Promise.resolve()
                    .then(() => {
                        this.statusManager.isSubmitting(true)
                        this.messageData({message: UPDATING_MESSAGE})
                    })
                    .then(() => this.submitAll())
                    .then(() => this.statusManager.removeCache('getcompany', 'getimporthrfile'))
                    .then(() => this.statusManager.do_getcompany())
                    .then(() => this.statusManager.do_getimporthrfile())
                    .then(() => this.mutableSyncCycle.init())
                    .then(() => {
                        this.crudMode('read')
                        this.statusManager.isSubmitting(false)
                        this.messageData(null)
                        global.snackbarData({message: 'synccycle.syncstep.message.success'})
                    })
                    .catch((reason: string) => {
                        return Promise.resolve()
                            .then(() => this.statusManager.removeCache('getcompany', 'getimporthrfile'))
                            .then(() => this.statusManager.do_getcompany())
                            .then(() => this.statusManager.do_getimporthrfile())
                            .then(() => {
                                this.messageData(createErrorMessage(String(reason)))
                                this.statusManager.isSubmitting(false)
                            })
                    })
            })
            .catch((reason: string) => {
                this.isExpanded(true)
                this.statusManager.isSubmitting(false)
                this.messageData(createErrorMessage(String(reason)))
            })
    }

    public submitAll() {
        switch (this.crudMode()) {
            case "create":
                return Promise.resolve()
                    .then(() => this.statusManager.do_createcompanysyncstep(this.getSubmitData()))
                    .then((data: [ICompanySyncstep]) => this.companySyncstep = data[0])
                    .then(() => {
                        this.mutableSyncstepFilters.setCompanySyncstep(this.companySyncstep)
                        this.mutableSyncstepFilters.submitAll()
                    })
                    .then(() => this.submitAllSyncstepParameters())
                    .then(() => this.statusManager.removeCache('getallcompanysyncstepparameters'))
            case "update":
                return Promise.resolve()
                    .then(() => this.statusManager.do_updatecompanysyncstep(this.getSubmitData()))
                    .then(() => this.mutableSyncstepFilters.submitAll())
                    .then(() => this.submitAllSyncstepParameters())
                    .then(() => this.statusManager.removeCache('getallcompanysyncstepparameters'))
            case "delete":
                return Promise.resolve()
                    .then(() => this.statusManager.do_deletecompanysyncstep(this.getSubmitData()))
        }
    }

    private doExpandToggle() {
        this.isExpanded(!this.isExpanded())
    }

    dispose() {
        this.crudMode.dispose()
        const domainError = this.errorObjects.domain
        const rulesetError = this.errorObjects.ruleset
        if (domainError) {
            this.em_tab_script.removeErrorObject(domainError)
        }
        if (rulesetError) {
            this.em_tab_script.removeErrorObject(rulesetError)
        }
    }
}